import StatsField from "../../../components/reusable/StatsField";
import {
  IconNews,
  IconCalendar,
  IconCoin,
  IconZoomMoney,
  IconLetterCase,
  IconOmega,
  IconBusinessplan,
  IconReceipt2,
  IconCircle,
  IconBracketsContainEnd,
  IconBracketsContainStart,
  IconForbid,
  IconRefresh,
  IconAlertTriangle
} from "@tabler/icons";
import CopyableText from "../../../components/reusable/CopyableText";
import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Paper,
  Space,
  Stack,
  Text
} from "@mantine/core";
import { Hbar } from "headstarter-crypto/hashgraph-sdk";
import ExpandableCard from "../../../components/reusable/ExpandableCard";
import HederaAddress from "../../../components/reusable/HederaAddress";
import ContractExecutorAnchor from "../../../components/reusable/ContractExecutorAnchor";
import ContractTrackingToggle from "../../../components/ContractTrackingToggle";
import ContractBalance from "../../../components/ContractBalance";
import TokenSupplyKeyToggle from "../../../components/TokenSupplyKeyToggle";
import MintingEnabledToggle from "../../../components/MintingEnabledToggle";
import BurningEnabledToggle from "../../../components/BurningEnabledToggle";

const INOContractInfo = ({ contract, onUpdate }) => {
  const { contractId, contractInfo } = contract || {};

  if (!contractInfo || !contractInfo.lastUpdated) {
    return (
      <Paper py="4rem" px="1rem" withBorder>
        <Center>
          <Stack spacing={5}>
            <Text size="lg" weight="bold" align="center">
              There is no info about this contract yet.
            </Text>
            <Text size="xs" color="dimmed" align="center">
              Check if the project is saved or the contract is already deployed.
            </Text>
            <Space mt="lg" />
            <Button
              leftIcon={<IconRefresh />}
              onClick={() => onUpdate(contractId)}
            >
              Refresh
            </Button>
          </Stack>
        </Center>
      </Paper>
    );
  }

  const { token } = contractInfo || {};

  return (
    <Stack>
      <ExpandableCard title="General">
        <ContractBalance contractId={contractId} />
        <Stack>
          <StatsField
            label="Contract ID"
            value={
              <Group position="apart" grow>
                <ContractExecutorAnchor
                  contractId={contractId}
                  abiFileName="StandardizedMerkleStorefront"
                >
                  <CopyableText copy={contractId}>
                    <HederaAddress contractId={contractId} />
                  </CopyableText>
                </ContractExecutorAnchor>
                <ContractTrackingToggle
                  contractId={contractId}
                  enabled={
                    !contractInfo.disabled || contractInfo.disabled === false
                  }
                  onCompleted={() => onUpdate(contractId)}
                />
              </Group>
            }
            icon={IconNews}
          />
          <StatsField
            label="Last Updated"
            date={contractInfo.lastUpdated}
            icon={IconCalendar}
          />
          <StatsField
            label="Last Ingestion"
            date={contractInfo.lastIngestion}
            icon={IconCalendar}
          />

          <StatsField
            label="Token Claim Max"
            value={contractInfo.tokenClaimMax}
            icon={IconForbid}
          />

          <StatsField
            label="Tokens sold"
            value={contractInfo.tokensSold}
            icon={IconZoomMoney}
          />
        </Stack>
      </ExpandableCard>

      <ExpandableCard title="Config" expanded={false}>
        <Stack>
          <Alert icon={<IconAlertTriangle />} color="yellow">
            <Text>
              Due to asynchronous updates, changes might not be reflected in
              <Text weight="bold" span>
                {" "}
                real-time
              </Text>
              . Refresh the page to view the most up-to-date status.
            </Text>
          </Alert>
          <MintingEnabledToggle
            contractId={contractId}
            contractInfo={contractInfo}
            onUpdate={onUpdate}
          />
          {contractInfo.hasOwnProperty("burningEnabled") && (
            <BurningEnabledToggle
              contractId={contractId}
              contractInfo={contractInfo}
              onUpdate={onUpdate}
            />
          )}
        </Stack>
      </ExpandableCard>

      <ExpandableCard title="Timing" expanded={false}>
        <Stack>
          <StatsField
            label="Start Time"
            date={contractInfo.startTime}
            icon={IconBracketsContainStart}
          />
          <StatsField
            label="Sale Start Time"
            date={contractInfo.saleStartTime}
            icon={IconBracketsContainStart}
          />
          <StatsField
            label="Token Gated Start Time"
            date={contractInfo.tokenGatedStartTime}
            icon={IconBracketsContainStart}
          />
          <StatsField
            label="Sale End Time"
            date={contractInfo.saleEndTime}
            icon={IconBracketsContainEnd}
          />
        </Stack>
      </ExpandableCard>

      <ExpandableCard title="Token Info" expanded={false}>
        <Stack>
          <StatsField
            label="Token ID"
            value={
              <CopyableText copy={token.tokenId}>
                <HederaAddress tokenId={token.tokenId} />
              </CopyableText>
            }
            icon={IconCoin}
          />
          <StatsField label="Name" value={token.name} icon={IconLetterCase} />
          <StatsField label="Symbol" value={token.symbol} icon={IconOmega} />
          {contractInfo.token.tokenId && (
            <TokenSupplyKeyToggle
              contractId={contractId}
              tokenId={contractInfo.token.tokenId}
            />
          )}
          <StatsField
            label="Initial Supply"
            value={token.initialSupply}
            icon={IconBusinessplan}
          />
          <StatsField
            label="Max Supply"
            value={token.maxSupply}
            icon={IconBusinessplan}
          />
          <StatsField
            label="Max Supply For Sale"
            value={token.maxSupplyForSale}
            icon={IconBusinessplan}
          />
          <StatsField
            label="Total Supply"
            value={token.totalSupply}
            icon={IconBusinessplan}
          />
          <StatsField
            label="Price Per Token"
            value={Hbar.fromTinybars(contractInfo.pricePerToken).toString()}
            icon={IconReceipt2}
          />
          <StatsField
            label="Metadata"
            value={<CopyableText text={token.metadata} />}
            icon={IconCoin}
          />
          <StatsField
            label="Metadata Unique"
            value={
              <Badge size="xs" color={token.metadataUnique ? "green" : "blue"}>
                {token.metadataUnique.toString()}
              </Badge>
            }
            icon={IconCircle}
          />
        </Stack>
      </ExpandableCard>
    </Stack>
  );
};

export default INOContractInfo;
